// Star SVG path data
const STAR_PATH =
  'M9.04896 3.42664C9.34833 2.50537 10.6517 2.50538 10.951 3.42664L12.0208 6.71864C12.1547 7.13063 12.5386 7.40957 12.9718 7.40958L16.4333 7.40971C17.402 7.40975 17.8047 8.6493 17.0211 9.21871L14.2208 11.2534C13.8703 11.508 13.7237 11.9594 13.8575 12.3714L14.927 15.6635C15.2263 16.5847 14.1719 17.3508 13.3882 16.7815L10.5878 14.747C10.2373 14.4924 9.76272 14.4924 9.41225 14.747L6.61179 16.7815C5.82809 17.3508 4.77367 16.5847 5.07297 15.6635L6.14249 12.3714C6.27634 11.9594 6.1297 11.508 5.77924 11.2534L2.97894 9.21871C2.19528 8.6493 2.59804 7.40975 3.56672 7.40971L7.02818 7.40958C7.46137 7.40957 7.8453 7.13063 7.97918 6.71864L9.04896 3.42664Z';

// Class to handle Trustpilot product ratings on product cards
class TrustpilotProductCardRating {
  constructor(config) {
    this.businessUnitId = config.businessUnitId;
    this.apiKey = config.apiKey;
    this.minRating = config.minRating;
    this.filledColor = config.filledColor || '#A2C75A';
    this.emptyColor = config.emptyColor || '#C8BDAA';
    this.productCards = document.querySelectorAll('.js-product-card-rating');
    this.cache = new Map();
    this.gradientIdCounter = 0;
  }

  // Fetch product reviews summary from Trustpilot API
  async fetchProductRating(skus) {
    // Check cache first
    const cacheKey = skus;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const response = await fetch(
        `https://api.trustpilot.com/v1/product-reviews/business-units/${this.businessUnitId}?sku=${encodeURIComponent(skus)}`,
        {
          headers: {
            ApiKey: this.apiKey,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console -- Trustpilot error fetching product rating
      console.error('Error fetching product card rating:', error);
      return null;
    }
  }

  // Create a single star SVG with gradient fill
  createStarSvg(fillPercentage) {
    const gradientId = `star-gradient-${this.gradientIdCounter++}`;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 20 21');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('class', 'product-card__rating-star');
    svg.setAttribute('aria-hidden', 'true');

    // Create gradient definition
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    gradient.setAttribute('id', gradientId);

    // Filled portion
    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop1.setAttribute('offset', `${fillPercentage}%`);
    stop1.setAttribute('stop-color', this.filledColor);

    // Empty portion
    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop2.setAttribute('offset', `${fillPercentage}%`);
    stop2.setAttribute('stop-color', this.emptyColor);

    gradient.appendChild(stop1);
    gradient.appendChild(stop2);
    defs.appendChild(gradient);
    svg.appendChild(defs);

    // Create star path
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', STAR_PATH);
    path.setAttribute('fill', `url(#${gradientId})`);

    svg.appendChild(path);

    return svg;
  }

  // Create star rating with inline SVGs using gradient fills
  renderStars(rating) {
    const container = document.createElement('div');
    container.className = 'product-card__rating-stars';

    for (let i = 0; i < 5; i++) {
      // Calculate fill percentage for this star
      // e.g., rating 3.5: star 0-2 = 100%, star 3 = 50%, star 4 = 0%
      const fillPercentage = Math.min(100, Math.max(0, (rating - i) * 100));
      const starSvg = this.createStarSvg(fillPercentage);
      container.appendChild(starSvg);
    }

    return container;
  }

  // Render the rating for a single product card
  renderRating(card, data) {
    const starsAverage = data.starsAverage;
    const totalReviews = data.numberOfReviews?.total;

    // Hide if no rating data or below minimum rating
    if (!starsAverage || !totalReviews || starsAverage < this.minRating) {
      return;
    }

    // Create stars
    const starsElement = this.renderStars(starsAverage);

    // Create review count
    const countSpan = document.createElement('span');
    countSpan.className = 'product-card__rating-count body-xxs';
    countSpan.textContent = `(${totalReviews.toLocaleString()})`;

    // Populate the card rating container
    card.innerHTML = '';
    card.appendChild(starsElement);
    card.appendChild(countSpan);
    card.classList.add('is-loaded');
  }

  // Process a single product card
  async processCard(card) {
    const skus = card.dataset.skus;
    if (!skus) return;

    const data = await this.fetchProductRating(skus);
    if (data) {
      this.renderRating(card, data);
    }
  }

  // Initialize and process all product cards
  async init() {
    if (!this.productCards.length) return;

    // Process cards with a small delay between each to avoid rate limiting
    const cards = Array.from(this.productCards);
    for (const card of cards) {
      await this.processCard(card);
    }
  }
}

// Start product card rating component when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const config = window.trustpilotProductCardConfig || {};
  if (config.businessUnitId && config.apiKey) {
    const trustpilotProductCardRating = new TrustpilotProductCardRating(config);
    trustpilotProductCardRating.init();
  }
});
